<template>
  <div id="app" :dir="isRtl ? 'rtl' : 'ltr'">
    <header class="app-header">
      <div class="brand">
        <span class="logo">
          <a href="https://pinesoftware.net" target="_blank" rel="noopener">
            <img src="https://pinesoftware.net/assets/images/pine.svg" alt="Logo" width="35" height="35" />
          </a>
        </span>
      </div>
      <div class="header-actions">
        <label class="language">
          <span>{{ t('language') }}</span>
          <select class="language-selection" v-model="locale">
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
            <option value="ar">العربية</option>
            <option value="es">Español</option>
          </select>
        </label>
      </div>
    </header>
    <main class="app-content">
      <router-view />
    </main>
    <footer v-if="businessData" class="business-bar">
      <div class="business-bar-content">
        <div class="business-owner">
          <div v-if="businessData.logoUrl" class="owner-logo">
            <img :src="businessData.logoUrl" alt="İşletme logosu" />
          </div>
          <div>
            <strong>{{ businessData.businessName }}</strong>
            <div v-if="businessData.hashtags?.length" class="business-hashtags">
              <span v-for="tag in businessData.hashtags" :key="tag" class="tag">{{ tag }}</span>
            </div>
          </div>
        </div>
        <div class="bottom-social-bar">
          <div v-if="businessData.socialLinks?.length" class="social-links">
            <div class="links">
              <a
                v-for="link in businessData.socialLinks"
                :key="link"
                class="social-link"
                :href="link"
                target="_blank"
                rel="noopener"
              >
                <span class="social-icon" :class="getSocialType(link)">
                  <span v-if="getSocialType(link) === 'instagram'">
                    <img src="/icons/instagram.png" alt="Instagram" width="14" height="14" />
                  </span>
                  <span v-else-if="getSocialType(link) === 'tiktok'">
                    <img src="/icons/tiktok.png" alt="TikTok" width="14" height="14" />
                  </span>
                  <span v-else-if="getSocialType(link) === 'twitter'">
                    <img src="/icons/twitter.png" alt="Twitter" width="14" height="14" />
                  </span>
                  <span v-else-if="getSocialType(link) === 'pinterest'">
                    <img src="/icons/pinterest.png" alt="Pinterest" width="14" height="14" />
                  </span>
                  <span v-else>
                    <span class="link-prefix">www</span>
                  </span>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from './i18n'
import { getEventBusiness } from './services/publicApi'

export default {
  name: 'App',
  setup() {
    const { locale, t } = useI18n()
    const isRtl = computed(() => locale.value === 'ar')
    const route = useRoute()
    const businessData = ref(null)

    const loadBusiness = async (eventSlug) => {
      if (!eventSlug) {
        businessData.value = null
        return
      }
      try {
        businessData.value = await getEventBusiness(eventSlug)
      } catch (error) {
        businessData.value = null
      }
    }

    const getSocialType = (link) => {
      const lower = link.toLowerCase()
      if (lower.includes('instagram.com')) return 'instagram'
      if (lower.includes('tiktok.com')) return 'tiktok'
      if (lower.includes('twitter.com') || lower.includes('x.com')) return 'twitter'
      if (lower.includes('pinterest.com')) return 'pinterest'
      return 'www'
    }

    watch(
      () => route.params.eventSlug,
      (eventSlug) => {
        loadBusiness(eventSlug)
      },
      { immediate: true }
    )

    return {
      locale,
      t,
      isRtl,
      businessData,
      getSocialType
    }
  }
}
</script>

<style>
* {
  box-sizing: border-box;
}

.bottom-social-bar {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
}

.business-hashtags {
  margin-top: 5px;
}

body {
  margin: 0;
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
  background: #f8fafc;
  color: #0f172a;
}

#app {
  min-height: 100vh;
  display: grid;
  grid-template-rows: auto 1fr;
}

.social-icon span {
  display: inline-block;
  color: white;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
}

.brand {
  display: flex;
  align-items: center;
  gap: 14px;
}

.logo {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: black;
  color: #ffffff;
  font-weight: 700;
}

.brand h1 {
  margin: 0;
  font-size: 20px;
}

.brand p {
  margin: 2px 0 0;
  color: #64748b;
  font-size: 13px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.language {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #0f172a;
}

.language select {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 6px 10px;
  background: #ffffff;
}

.nav-link {
  text-decoration: none;
  color: #0f172a;
  font-weight: 600;
}

.app-content {
  padding: 28px;
  padding-bottom: 180px;
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
}

.business-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: #0f172a;
  color: #f8fafc;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  z-index: 50;
  padding: 12px 20px;
}

.business-bar-content {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.business-owner {
  display: flex;
  align-items: center;
  gap: 12px;
}

.owner-logo {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  overflow: hidden;
  background: #1e293b;
  flex-shrink: 0;
}

.owner-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.label {
  display: block;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 4px;
}

.social-link {
  color: #f8fafc;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.social-links .links {
  display: flex;
  align-items: center;
  gap: 8px;
}

.social-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #1e293b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.social-icon.instagram {
  background: white;
}

.social-icon.tiktok {
  background: white;
}

.social-icon.twitter {
  background: white;
}

.social-icon.pinterest {
  background: white;
}

.link-prefix {
  font-weight: 700;
}

.hashtags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  background: rgba(148, 163, 184, 0.2);
  color: #f8fafc;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}
</style>
